import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Popover,
  Typography,
  Stack,
  ButtonBase,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import {
  TranslateOutlined,
  ExpandMore,
  CheckRounded,
} from "@mui/icons-material";
import { GlobalContext } from "../context/GlobalContext";
import { TranslateContext } from "../context/TranslateContext";

const ChangeLang = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { hitAxios } = useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const p = theme.palette.primary;

  const [anchorEl, setAnchorEl] = useState(null);
  const [langs, setLangs] = useState([]);
  const open = Boolean(anchorEl);

  const currentLang = localStorage.getItem("language") || "english";

  async function getLangs() {
    try {
      const res = await hitAxios({
        path: "/api/web/get-all-translation-name",
        admin: false,
        post: false,
      });
      if (res.data.success) setLangs(res.data.data);
    } catch (e) {}
  }

  useEffect(() => {
    getLangs();
  }, []);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (lang) => {
    localStorage.setItem("language", lang.replace(".json", ""));
    window.location.reload();
  };

  const formatName = (name) =>
    name
      .replace(".json", "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <>
      {/* ── Trigger button ── */}
      <ButtonBase
        onClick={handleOpen}
        disableRipple
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.6,
          borderRadius: "10px",
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark
            ? alpha(theme.palette.background.paper, 0.5)
            : theme.palette.background.paper,
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: alpha(p.main, isDark ? 0.3 : 0.2),
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.8)
              : alpha(p.main, 0.03),
          },
        }}
      >
        <TranslateOutlined sx={{ fontSize: 14, color: "primary.main" }} />
        <Typography
          variant="caption"
          fontWeight={600}
          fontSize={12}
          color="text.secondary"
          sx={{ textTransform: "capitalize" }}
        >
          {formatName(currentLang)}
        </Typography>
        <ExpandMore
          sx={{
            fontSize: 14,
            color: "text.disabled",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </ButtonBase>

      {/* ── Dropdown ── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: -0.75,
              borderRadius: "14px",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: isDark
                ? alpha(theme.palette.background.paper, 0.95)
                : theme.palette.background.paper,
              backdropFilter: "blur(20px)",
              boxShadow: isDark
                ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
                : `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
              minWidth: 160,
              maxHeight: 260,
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            fontSize={11}
            color="text.disabled"
            letterSpacing={0.6}
          >
            {lang?.selectLanguage || "SELECT LANGUAGE"}
          </Typography>
        </Box>

        <Divider sx={{ opacity: 0.6 }} />

        {/* Language list */}
        <Stack
          sx={{
            py: 0.75,
            maxHeight: 200,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(p.main, 0.2),
              borderRadius: "4px",
            },
          }}
        >
          {langs.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.disabled" fontSize={12}>
                No languages found
              </Typography>
            </Box>
          ) : (
            langs.map((lang, i) => {
              const name = lang.replace(".json", "");
              const isActive = currentLang === name;

              return (
                <ButtonBase
                  key={i}
                  onClick={() => handleSelect(lang)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    gap: 1.5,
                    transition: "all 0.15s ease",
                    bgcolor: isActive
                      ? alpha(p.main, isDark ? 0.12 : 0.07)
                      : "transparent",
                    "&:hover": {
                      bgcolor: isActive
                        ? alpha(p.main, isDark ? 0.18 : 0.1)
                        : isDark
                          ? alpha(theme.palette.background.default, 0.5)
                          : alpha(p.main, 0.04),
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 700 : 500}
                    fontSize={13}
                    color={isActive ? "primary.main" : "text.primary"}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {formatName(lang)}
                  </Typography>

                  {isActive && (
                    <CheckRounded
                      sx={{
                        fontSize: 14,
                        color: "primary.main",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </ButtonBase>
              );
            })
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default ChangeLang;
